// The Purchase Orders page's reads and read rules: ONE copy, used by the desktop Purchase Orders page
// (pages/PurchaseOrdersPage.jsx, POSalesOrderEarmarks) and the read-only phone page
// (mobile/screens/PurchaseOrdersScreen.jsx). docs/mobile-pwa-plan.md "One copy of every action".
// Moved from the page; pages/PurchaseOrdersPage.reads.actions.test.jsx pins the desktop's reads.
import config from '../../config.js';
import { apiErrorMessage } from '../../utils/apiError';

const API_BASE = config.API_BASE_URL;

// --- The list --------------------------------------------------------------------------------------

/** The list's status filter, as the desktop offers it. */
export const PO_STATUS_OPTIONS = Object.freeze([
  { value: '', label: 'All' },
  { value: 'RFQ', label: 'RFQ / Bids' },
  { value: 'Committed', label: 'Committed' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'Converted', label: 'Converted (awarded RFQ)' },
]);

/** GET /purchase_orders (?status, ?vendor_sales_order): every PO with its lines. Throws. */
export async function loadPurchaseOrders({ status, vendorSalesOrder, signal } = {}) {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (vendorSalesOrder) params.append('vendor_sales_order', vendorSalesOrder);
  const queryString = params.toString();
  const url = `${API_BASE}/purchase_orders${queryString ? `?${queryString}` : ''}`;
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`Failed to list purchase orders: ${res.status}`);
  return res.json();
}

/** ?po=<PO #> (the desktop's deep link, e.g. from the finance PO Audit page): that PO from the
 *  UNFILTERED list (so Completed / RFQ POs open too), or null. "01380" finds PO 1380. Throws when the list
 *  can't be read. */
export async function findPurchaseOrderByNumber(poNumber) {
  const target = String(parseInt(poNumber, 10));
  const all = await loadPurchaseOrders();
  return (all || []).find((p) => String(p.po_number) === target) || null;
}

/** The rows a search shows: PO #, supplier or project contains it; never a row outside the selected
 *  status (the list can briefly hold the dashboard's unfiltered payload). */
export function filterPurchaseOrders(list, { searchTerm = '', statusFilter = '' } = {}) {
  const term = String(searchTerm || '').toLowerCase();
  return (list || []).filter((po) => {
    const matchesTerm = (
      String(po.po_number).toLowerCase().includes(term) ||
      (po.supplier || '').toLowerCase().includes(term) ||
      (po.project_name || '').toLowerCase().includes(term)
    );
    const matchesStatus = !statusFilter || po.status === statusFilter;
    return matchesTerm && matchesStatus;
  });
}

/** A bundle has no row of its own: its project name lives on each of its POs. */
export function rfqBundleName(grp) {
  return ((grp && (grp.childPOs || [grp])) || []).map((p) => p.project_name).find(Boolean) || '';
}

/** RFQ POs of one rfq_group_id fold into one header row ({...firstPO, isGroupHeader, childPOs}), in
 *  list order. A one-vendor RFQ opens as a plain PO (the "send this PO out for one quote, then order"
 *  flow); 2+ vendors keep the comparison bundle. */
export function groupRfqBundles(pos) {
  const grouped = [];
  const rfqGroups = {};
  (pos || []).forEach((po) => {
    if (po.status === 'RFQ' && po.rfq_group_id) {
      if (!rfqGroups[po.rfq_group_id]) {
        rfqGroups[po.rfq_group_id] = { ...po, isGroupHeader: true, childPOs: [] };
        grouped.push(rfqGroups[po.rfq_group_id]);
      }
      rfqGroups[po.rfq_group_id].childPOs.push(po);
    } else {
      grouped.push(po);
    }
  });
  for (let i = 0; i < grouped.length; i++) {
    const g = grouped[i];
    if (g.isGroupHeader && g.childPOs && g.childPOs.length === 1) grouped[i] = g.childPOs[0];
  }
  return grouped;
}

/** A Committed PO past its estimated receive date with a line still to come. `today` = YYYY-MM-DD.
 *  {isLate, daysLate}. */
export function poLateInfo(po, today) {
  const hasOutstanding = Array.isArray(po.lines) && po.lines.some((ln) => (ln.quantity_ordered || 0) > (ln.quantity_received || 0));
  const isLate = po.status === 'Committed' && !!po.estimated_receive_date && po.estimated_receive_date < today && hasOutstanding;
  const daysLate = isLate ? Math.floor((new Date(today) - new Date(po.estimated_receive_date)) / 86400000) : 0;
  return { isLate, daysLate };
}

// --- One PO --------------------------------------------------------------------------------------------

/** GET /purchase_orders/{id}: header, lines, shipments (receipt lines, heat #s, TRKs, certs, packing
 *  lists all ride inside). Throws. */
export async function loadPurchaseOrder(purchaseOrderId) {
  const res = await fetch(`${API_BASE}/purchase_orders/${purchaseOrderId}`);
  if (!res.ok) throw new Error(`Failed to get PO detail: ${res.status}`);
  return res.json();
}

/** Payment terms as the desktop's terms box reads them: blank = Net 30 (the API's default),
 *  0 = due on receipt. */
export function poTermsLabel(days) {
  if (days === null || days === undefined || days === '') return 'Net 30 (default)';
  return Number(days) === 0 ? 'Due on receipt' : `Net ${days}`;
}

/** Σ ordered × unit cost. */
export function poLinesTotal(lines) {
  return (lines || []).reduce((acc, ln) => {
    const qty = parseFloat(ln.quantity_ordered) || 0;
    const cost = parseFloat(ln.cost_per_unit) || 0;
    return acc + (qty * cost);
  }, 0);
}

/** Still to come on a line (never below 0). */
export function lineRemaining(ln) {
  const remain = (ln.quantity_ordered || 0) - (ln.quantity_received || 0);
  return remain > 0 ? remain : 0;
}

/** A line's receipts: every shipment line received against it, with its shipment's date, in shipment
 *  order (heat #, TRK, certs). PO lines carry no heat number of their own. */
export function receiptsByLine(shipments) {
  const out = {};
  (shipments || []).forEach((sh) => {
    (sh.lines || []).forEach((sl) => {
      (out[sl.po_line_id] = out[sl.po_line_id] || []).push({ ...sl, shipment_id: sh.shipment_id, shipment_date: sh.shipment_date });
    });
  });
  return out;
}

/** A shipment's own documents (packing lists): the ones not filed on one of its lines (certs are). */
export function packingListDocs(shipment) {
  return ((shipment && shipment.documents) || []).filter((d) => !d.shipment_line_id);
}

/** Where a receiving document (cert, packing list) opens. */
export function poDocUrl(doc) {
  return `https://jobq-images.s3.us-east-2.amazonaws.com/${doc.s3_key}`;
}

/** The PO's PDF (the API key rides on fetch: utils/openPdf on the desktop, PdfButton on a phone). */
export function poPdfUrl(purchaseOrderId) {
  return `${API_BASE}/purchase_orders/${purchaseOrderId}/pdf`;
}

/** GET /purchase_orders/{id}/email_history: the emails sent for the PO, newest first. */
export async function loadPoEmailHistory(purchaseOrderId) {
  const res = await fetch(`${API_BASE}/purchase_orders/${purchaseOrderId}/email_history`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/** An email's time as the office reads it: Central, "10/2/2026, 2:39 PM". */
export function emailSentAt(dateString) {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** GET /purchase_orders/{id}/allocations: {allocations, suggestions} (the earmarks for sales orders and
 *  the derived hints). Throws the API's sentence. */
export async function loadPoEarmarks(purchaseOrderId) {
  const res = await fetch(`${API_BASE}/purchase_orders/${purchaseOrderId}/allocations`);
  if (!res.ok) throw new Error(await apiErrorMessage(res));
  const data = await res.json();
  return {
    allocations: Array.isArray(data.allocations) ? data.allocations : [],
    suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
  };
}

/** The earmarks still held, by PO line. */
export function activeEarmarksByLine(allocations) {
  const m = {};
  (allocations || []).forEach((a) => {
    if (a.released_at) return;
    (m[a.po_line_id] = m[a.po_line_id] || []).push(a);
  });
  return m;
}
